import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, UsersIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/layout';
import {
  Badge,
  Button,
  Card,
  ErrorSummary,
  Input,
  LoadingSpinner,
  Modal,
  ModalFooter,
  Select,
} from '@/components/common';
import { applyServerErrors } from '@/utils/formErrors';
import { phoneSchema, digitsOnlyOnChange, isRepeatedDigitPhone, nameAsTypedOnChange } from '@/utils/validation';
import { personNameProblem } from '@/utils/format';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import api, { getErrorMessage } from '@/services/api';
import { insurerRolesService, type AccessRoleChoice } from '@/services/insurerRoles';

/**
 * Insurer admin > Team (24 Sep batch): "Manager and Staff user creation
 * delegated to Insurance Company Admins".
 *
 * Backed by `/insurers/insurance/team/` (`InsurerTeamViewSet`), which is scoped
 * to the admin's OWN insurer tenant. The admin can add Managers and Staff only
 * -- never another Admin, which stays the country admin's call -- and can
 * suspend, reactivate, remove or reset the password of a Manager/Staff login. Rows the
 * server will not let them act on (other admins, themselves) show no actions:
 * `manageable` comes from the server so the buttons and the rule cannot drift.
 *
 * No delivery channel works on this platform, so the temporary password is
 * generated server-side and shown here exactly once.
 *
 * #397: each member's Access is Admin, one of the insurer's custom roles
 * (Roles page), or Standard access. Change Access is offered on every row but
 * the viewer's own or another Admin's; it never assigns Admin (only the country
 * admin creates or changes Insurer Admins) and the server refuses any role or
 * member outside this insurer.
 */

export interface TeamMember {
  id: string;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  phone: string | null;
  role: string;
  company_role: 'ADMIN' | 'MANAGER' | 'STAFF';
  status: 'active' | 'suspended' | 'blocked';
  is_active: boolean;
  must_change_password: boolean;
  last_login: string | null;
  created_at: string;
  is_self: boolean;
  manageable: boolean;
  /** #397: "admin", the custom role id, or null (Standard access). */
  access_role?: AccessRoleChoice;
  access_role_name?: string;
}

interface Credentials {
  email: string;
  phone: string | null;
  password: string;
  message?: string;
}

const TEAM_URL = '/insurers/insurance/team/';
const QUERY_KEY = ['insurer-team'];
const ROLES_KEY = ['insurer-roles'];
const STANDARD_ACCESS = '';

/** The insurer's custom roles, fetched only while a picker is open. */
function useRoleOptions(enabled: boolean) {
  return useQuery({ queryKey: ROLES_KEY, queryFn: () => insurerRolesService.list(), enabled });
}

const ROLE_LABELS: Record<TeamMember['company_role'], string> = {
  ADMIN: 'Admin',
  MANAGER: 'Manager',
  STAFF: 'Staff',
};

const STATUS_VARIANT: Record<TeamMember['status'], 'success' | 'warning' | 'error'> = {
  active: 'success',
  suspended: 'warning',
  blocked: 'error',
};

// #361: names are stored as typed ("QA" stays "QA"); only digits and symbols are refused.
const nameField = (required: string) =>
  z
    .string()
    .min(1, required)
    .superRefine((value, ctx) => {
      const problem = personNameProblem(value);
      if (problem) ctx.addIssue({ code: z.ZodIssueCode.custom, message: problem });
    });

// #361: "0000000000" (any one digit repeated) is a placeholder, not a
// mobile number. The server refuses it too.
const mobileField = phoneSchema.refine((value) => !isRepeatedDigitPhone(value), {
  message: 'Enter a valid mobile number',
});

const schema = z.object({
  first_name: nameField('First name is required'),
  last_name: nameField('Last name is required'),
  email: z.string().min(1, 'Email is required').email('Invalid email'),
  phone: mobileField,
  role: z.enum(['MANAGER', 'STAFF'], { errorMap: () => ({ message: 'Role is required' }) }),
  custom_role: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

function AddMemberModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (credentials: Credentials) => void;
}) {
  const [serverMessages, setServerMessages] = useState<string[]>([]);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema), defaultValues: { role: 'STAFF', custom_role: STANDARD_ACCESS } });
  const roles = useRoleOptions(true);

  const onSubmit = async (values: FormData) => {
    setServerMessages([]);
    let credentials: Credentials;
    const { custom_role: customRole, ...rest } = values;
    const payload = customRole ? { ...rest, custom_role: customRole } : rest;
    try {
      const response = await api.post(TEAM_URL, payload);
      credentials = response.data?.credentials;
    } catch (error) {
      setServerMessages(applyServerErrors(error, (name, err) => setError(name as keyof FormData, err)));
      return;
    }
    if (!credentials?.password) {
      // The account may exist but nobody can sign in with it; say so rather
      // than closing on a success nobody can use.
      setServerMessages(['The user was created but no password came back. Use Reset Password on their row.']);
      return;
    }
    onCreated(credentials);
  };

  return (
    <Modal isOpen onClose={onClose} title="Add Team Member" size="lg">
      <form onSubmit={handleSubmit(onSubmit)}>
        <ErrorSummary messages={serverMessages} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            id="team-first-name"
            label="First Name"
            required
            error={errors.first_name?.message}
            {...register('first_name', { onChange: nameAsTypedOnChange })}
          />
          <Input
            id="team-last-name"
            label="Last Name"
            required
            error={errors.last_name?.message}
            {...register('last_name', { onChange: nameAsTypedOnChange })}
          />
          <Input
            id="team-email"
            label="Email"
            type="email"
            required
            error={errors.email?.message}
            {...register('email')}
          />
          <Input
            id="team-phone"
            label="Phone"
            required
            error={errors.phone?.message}
            {...register('phone', { onChange: digitsOnlyOnChange })}
          />
          <Select
            id="team-role"
            label="Role"
            required
            error={errors.role?.message}
            options={[
              { value: 'MANAGER', label: 'Manager' },
              { value: 'STAFF', label: 'Staff' },
            ]}
            {...register('role')}
          />
          <Select
            id="team-access-role"
            label="Access"
            options={[
              { value: STANDARD_ACCESS, label: 'Standard access' },
              ...(roles.data ?? []).map((r) => ({ value: r.id, label: r.name })),
            ]}
            {...register('custom_role')}
          />
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          A temporary password is generated and shown once after you save. Admins are created
          only by the country administrator.
        </p>
        <ModalFooter>
          <Button variant="outline" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" isLoading={isSubmitting}>
            Add Member
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

const editSchema = z.object({
  first_name: nameField('First name is required'),
  last_name: nameField('Last name is required'),
  phone: mobileField,
});

type EditFormData = z.infer<typeof editSchema>;

/** #361: Edit Member. Name and mobile only; the e-mail is fixed once created. */
function EditMemberModal({ member, onClose }: { member: TeamMember; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [serverMessages, setServerMessages] = useState<string[]>([]);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<EditFormData>({
    resolver: zodResolver(editSchema),
    defaultValues: { first_name: member.first_name, last_name: member.last_name, phone: member.phone ?? '' },
  });

  const onSubmit = async (values: EditFormData) => {
    setServerMessages([]);
    try {
      await api.patch(`${TEAM_URL}${member.id}/`, values);
    } catch (error) {
      setServerMessages(applyServerErrors(error, (name, err) => setError(name as keyof EditFormData, err)));
      return;
    }
    queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    toast.success(`${member.full_name || member.email} updated`);
    onClose();
  };

  return (
    <Modal isOpen onClose={onClose} title={`Edit member: ${member.full_name || member.email}`} size="lg">
      <form onSubmit={handleSubmit(onSubmit)}>
        <ErrorSummary messages={serverMessages} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            id="team-edit-first-name"
            label="First Name"
            required
            error={errors.first_name?.message}
            {...register('first_name', { onChange: nameAsTypedOnChange })}
          />
          <Input
            id="team-edit-last-name"
            label="Last Name"
            required
            error={errors.last_name?.message}
            {...register('last_name', { onChange: nameAsTypedOnChange })}
          />
          <Input
            id="team-edit-phone"
            label="Phone"
            required
            error={errors.phone?.message}
            {...register('phone', { onChange: digitsOnlyOnChange })}
          />
          <Input id="team-edit-email" label="Email" value={member.email} readOnly disabled helperText="Email cannot be changed." />
        </div>
        <ModalFooter>
          <Button variant="outline" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" isLoading={isSubmitting}>
            Save Changes
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

/** #361: Change Role, Manager <-> Staff. Custom access is Change Access. */
function ChangeRoleModal({ member, onClose }: { member: TeamMember; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [role, setRole] = useState<string>(member.company_role === 'MANAGER' ? 'MANAGER' : 'STAFF');
  const [errors, setErrors] = useState<string[]>([]);

  const change = useMutation({
    mutationFn: () => api.post(`${TEAM_URL}${member.id}/change_role/`, { role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success(`Role updated for ${member.full_name || member.email}`);
      onClose();
    },
    onError: (err) => setErrors([getErrorMessage(err)]),
  });

  return (
    <Modal isOpen onClose={onClose} title={`Change role: ${member.full_name || member.email}`} size="md">
      <ErrorSummary messages={errors} />
      <Select
        id="team-change-role"
        label="Role"
        value={role}
        onChange={(e) => setRole(e.target.value)}
        options={[
          { value: 'MANAGER', label: 'Manager' },
          { value: 'STAFF', label: 'Staff' },
        ]}
      />
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
        The member is signed out so the new role applies on their next sign-in.
      </p>
      <ModalFooter>
        <Button variant="outline" type="button" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" isLoading={change.isPending} onClick={() => change.mutate()}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function ChangeAccessModal({ member, onClose }: { member: TeamMember; onClose: () => void }) {
  const queryClient = useQueryClient();
  const roles = useRoleOptions(true);
  const [choice, setChoice] = useState<string>(member.access_role ?? STANDARD_ACCESS);
  const [errors, setErrors] = useState<string[]>([]);

  const assign = useMutation({
    mutationFn: () => insurerRolesService.assign(member.id, choice || null),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ROLES_KEY });
      toast.success(`Access updated for ${member.full_name || member.email}`);
      onClose();
    },
    onError: (err) => setErrors([getErrorMessage(err)]),
  });

  return (
    <Modal isOpen onClose={onClose} title={`Change access: ${member.full_name || member.email}`} size="md">
      <ErrorSummary messages={errors} />
      <Select
        id="team-change-access"
        label="Access"
        value={choice}
        onChange={(e) => setChoice(e.target.value)}
        options={[
          { value: STANDARD_ACCESS, label: 'Standard access' },
          ...(roles.data ?? []).map((r) => ({ value: r.id, label: r.name })),
        ]}
      />
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
        Admins are created and changed only by the country administrator.
      </p>
      <ModalFooter>
        <Button variant="outline" type="button" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" isLoading={assign.isPending} onClick={() => assign.mutate()}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function CredentialsModal({ credentials, onClose }: { credentials: Credentials | null; onClose: () => void }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Modal isOpen={!!credentials} onClose={onClose} title="Temporary Password" size="md">
      {credentials && (
        <div className="space-y-4">
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 dark:bg-amber-900/20 dark:border-amber-800">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
              This password is shown only once.
            </p>
            <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
              Copy it and share it securely now. They must change it on first login.
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Login</p>
            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{credentials.email}</p>
          </div>
          <div>
            <label htmlFor="team-temp-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Temporary Password
            </label>
            <div className="mt-1 flex gap-2">
              <input
                id="team-temp-password"
                type="text"
                readOnly
                value={credentials.password}
                className="block w-full rounded-md border-gray-300 bg-gray-50 font-mono shadow-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
              />
              <Button type="button" variant="outline" onClick={() => handleCopy(credentials.password)}>
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </div>
        </div>
      )}
      <ModalFooter>
        <Button type="button" onClick={onClose}>
          Done
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export function TeamPage() {
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [credentials, setCredentials] = useState<Credentials | null>(null);
  const [removing, setRemoving] = useState<TeamMember | null>(null);
  const [changingAccess, setChangingAccess] = useState<TeamMember | null>(null);
  const [editing, setEditing] = useState<TeamMember | null>(null);
  const [changingRole, setChangingRole] = useState<TeamMember | null>(null);
  // Below md the table becomes cards (no sideways scroll on a phone).
  const isMobile = useMediaQuery('(max-width: 767px)');

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const response = await api.get(TEAM_URL, { params: { page_size: 100 } });
      const body = response.data;
      return (Array.isArray(body) ? body : (body?.results ?? [])) as TeamMember[];
    },
  });

  const actionMutation = useMutation({
    mutationFn: async ({ member, verb }: { member: TeamMember; verb: 'suspend' | 'activate' | 'reset_password' | 'resend_invitation' }) => {
      const response = await api.post(`${TEAM_URL}${member.id}/${verb}/`);
      return { verb, member, body: response.data };
    },
    onSuccess: ({ verb, member, body }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      if (verb === 'reset_password' || verb === 'resend_invitation') {
        if (body?.credentials?.password) {
          setCredentials(body.credentials);
        } else {
          toast.error('The password was reset but none came back. Try again.');
        }
        return;
      }
      toast.success(`${member.full_name || member.email} ${verb === 'suspend' ? 'suspended' : 'reactivated'}`);
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const removeMutation = useMutation({
    mutationFn: async (member: TeamMember) => {
      await api.post(`${TEAM_URL}${member.id}/remove/`);
      return member;
    },
    onSuccess: (member) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success(`${member.full_name || member.email} removed from your team`);
    },
    onError: (err) => toast.error(getErrorMessage(err)),
    onSettled: () => setRemoving(null),
  });

  const members = data ?? [];

  // The row actions, shared by the desktop table and the mobile cards. Only
  // rows the server marks `manageable` get member actions; Change Access is
  // offered on every row but the viewer's own and other Admins' (#397, #361).
  const renderActions = (member: TeamMember, busy: boolean, justify: string, touch = false) => {
    const size = touch ? 'min-h-[44px]' : '';
    const wrap = `flex flex-wrap gap-2 ${justify}`;
    if (member.is_self) return <span className="text-xs text-gray-400 dark:text-gray-500">—</span>;
    if (!member.manageable) {
      // #361: another Admin is the country admin's to change.
      if (member.role === 'insurer_admin') return <span className="text-xs text-gray-400 dark:text-gray-500">—</span>;
      return (
        <div className={wrap}>
          <Button size="sm" variant="outline" className={size} onClick={() => setChangingAccess(member)}>
            Change Access
          </Button>
        </div>
      );
    }
    const neverSignedIn = member.must_change_password && !member.last_login;
    return (
      <div className={wrap}>
        <Button size="sm" variant="outline" className={size} disabled={busy} onClick={() => setEditing(member)}>
          Edit
        </Button>
        {member.status === 'active' ? (
          <Button
            size="sm"
            variant="outline"
            className={size}
            disabled={busy}
            onClick={() => actionMutation.mutate({ member, verb: 'suspend' })}
          >
            Suspend
          </Button>
        ) : member.status === 'suspended' ? (
          <Button
            size="sm"
            variant="outline"
            className={size}
            disabled={busy}
            onClick={() => actionMutation.mutate({ member, verb: 'activate' })}
          >
            Reactivate
          </Button>
        ) : null}
        {neverSignedIn ? (
          <Button
            size="sm"
            variant="outline"
            className={size}
            disabled={busy}
            onClick={() => actionMutation.mutate({ member, verb: 'resend_invitation' })}
          >
            Resend Invitation
          </Button>
        ) : null}
        <Button
          size="sm"
          variant="outline"
          className={size}
          disabled={busy}
          onClick={() => actionMutation.mutate({ member, verb: 'reset_password' })}
        >
          Reset Password
        </Button>
        <Button size="sm" variant="outline" className={size} disabled={busy} onClick={() => setChangingRole(member)}>
          Change Role
        </Button>
        <Button size="sm" variant="outline" className={size} disabled={busy} onClick={() => setRemoving(member)}>
          Remove
        </Button>
        <Button size="sm" variant="outline" className={size} disabled={busy} onClick={() => setChangingAccess(member)}>
          Change Access
        </Button>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Team"
        subtitle="People who can sign in to your insurance company's portal"
        actions={
          <Button onClick={() => setShowAdd(true)} leftIcon={<PlusIcon className="h-4 w-4" />}>
            Add Member
          </Button>
        }
      />

      <Card>
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : isError ? (
          <div className="py-12 text-center" role="alert">
            <p className="text-sm text-red-600 dark:text-red-400">
              Could not load your team: {getErrorMessage(error)}
            </p>
            <Button variant="outline" className="mt-3" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        ) : members.length === 0 ? (
          <div className="py-12 text-center">
            <UsersIcon className="mx-auto h-10 w-10 text-gray-400 dark:text-gray-500" />
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">No team members yet.</p>
          </div>
        ) : isMobile ? (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700" aria-label="Team members">
            {members.map((member) => {
              const busy = actionMutation.isPending && actionMutation.variables?.member.id === member.id;
              return (
                <li key={member.id} data-testid={`team-card-${member.id}`} className="space-y-2 p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="break-words text-sm font-medium text-gray-900 dark:text-gray-100">
                        {member.full_name || member.email}
                        {member.is_self && <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">(you)</span>}
                      </p>
                      <p className="break-all text-sm text-gray-600 dark:text-gray-300">{member.email}</p>
                      {member.phone && <p className="text-xs text-gray-500 dark:text-gray-400">{member.phone}</p>}
                    </div>
                    <Badge variant={STATUS_VARIANT[member.status] ?? 'default'} size="sm">
                      {member.status.charAt(0).toUpperCase() + member.status.slice(1)}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    {ROLE_LABELS[member.company_role] ?? member.company_role}
                    {' · '}
                    {member.access_role_name ?? (member.role === 'insurer_admin' ? 'Admin' : 'Standard access')}
                  </p>
                  {renderActions(member, busy, 'justify-start', true)}
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Access</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                {members.map((member) => {
                  const busy = actionMutation.isPending && actionMutation.variables?.member.id === member.id;
                  return (
                    <tr key={member.id} data-testid={`team-row-${member.id}`}>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                        {member.full_name || member.email}
                        {member.is_self && <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">(you)</span>}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                        <div>{member.email}</div>
                        {member.phone && <div className="text-xs text-gray-500 dark:text-gray-400">{member.phone}</div>}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                        {ROLE_LABELS[member.company_role] ?? member.company_role}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                        {member.access_role_name ?? (member.role === 'insurer_admin' ? 'Admin' : 'Standard access')}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <Badge variant={STATUS_VARIANT[member.status] ?? 'default'} size="sm">
                          {member.status.charAt(0).toUpperCase() + member.status.slice(1)}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right text-sm">
                        {renderActions(member, busy, 'justify-end')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showAdd && (
        <AddMemberModal
          onClose={() => setShowAdd(false)}
          onCreated={(created) => {
            setShowAdd(false);
            queryClient.invalidateQueries({ queryKey: QUERY_KEY });
            setCredentials(created);
          }}
        />
      )}
      <CredentialsModal credentials={credentials} onClose={() => setCredentials(null)} />
      {editing && <EditMemberModal member={editing} onClose={() => setEditing(null)} />}
      {changingRole && <ChangeRoleModal member={changingRole} onClose={() => setChangingRole(null)} />}
      {changingAccess && <ChangeAccessModal member={changingAccess} onClose={() => setChangingAccess(null)} />}
      <Modal
        isOpen={removing !== null}
        onClose={() => setRemoving(null)}
        title="Remove from your team?"
        description={
          removing
            ? `${removing.full_name || removing.email} will be signed out and will no longer be able to reach your insurer portal. You can add them again later from Add Member.`
            : undefined
        }
      >
        <ModalFooter>
          <Button variant="outline" onClick={() => setRemoving(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            isLoading={removeMutation.isPending}
            onClick={() => removing && removeMutation.mutate(removing)}
          >
            Remove
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default TeamPage;
